import React, { Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { AGENCY_SHELL_HORIZONTAL_PX } from '../../constants/layoutSpacing';
import { PUBLIC_SHELL_MIN_HEIGHT } from '../../constants/clsShell';
import { LEARNING_LAB_BANNER_HEIGHT_CSS_VAR } from '../../constants/learningLabLayout';
import { shouldReservePublicShellMinHeight } from '../../utils/publicShellPaths';
import { isStudioBuilderV2ClientEnabled } from '../../studio/studioVisualFlags';
import Header from './Header';
import Footer from './Footer';
import ProviderAccessBanner from '../ProviderAccessBanner';
import ProviderAcquisitionPopupManager from '../provider-acquisition/ProviderAcquisitionPopupManager';
import TrainingAlertsNudgeManager from '../training-alerts/TrainingAlertsNudgeManager';
import UnifiedHelpSpeedDial from '../common/UnifiedHelpSpeedDial';
import SpeedDialErrorBoundary from '../common/SpeedDialErrorBoundary';
import { useBugReportHandler } from '../common/FloatingBugReportButton';
import BugReportDialog from '../common/BugReportDialog';
import { useTacticoreAuthoringChromeActive } from '../../hooks/useTacticoreAuthoringChromeActive';
import { TacticoreAuthoringMiniFooter } from '../tacticore/TacticoreAuthoringChrome';
import { TacticoreAuthoringChromeProvider } from '../tacticore/TacticoreAuthoringChromeContext';

const LayoutRouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', width: '100%' }}>
    <CircularProgress />
  </Box>
);

function isStudioBuilderImmersivePath(pathname: string): boolean {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path.includes('/dashboard/studio/builder/')) return true;
  // Legacy edit URL mounts Builder v2 when the client flag is on.
  if (
    /\/dashboard\/studio\/pages\/[^/]+\/edit$/.test(path) &&
    isStudioBuilderV2ClientEnabled()
  ) {
    return true;
  }
  return false;
}

/** Viewport under optional Learning Lab banner (banner sits above Layout in App). */
const STUDIO_BUILDER_LAYOUT_HEIGHT = `calc(100dvh - var(${LEARNING_LAB_BANNER_HEIGHT_CSS_VAR}, 0px))`;

const Layout: React.FC = () => {
  const location = useLocation();
  const bugReportHandler = useBugReportHandler();

  // Check if we're on the home page (tours should not be available on home page)
  const isHomePage = location.pathname === '/';
  const studioBuilderImmersive = isStudioBuilderImmersivePath(location.pathname);
  const tacticoreAuthoringImmersive = useTacticoreAuthoringChromeActive();
  const immersiveChrome = studioBuilderImmersive || tacticoreAuthoringImmersive;
  // Public entity detail + listing routes lazy-load into a short main; reserve height so the footer
  // does not park in the Lighthouse viewport (PSI CLS attributed to footer ~0.4+).
  const reserveMainMinHeight =
    isHomePage || shouldReservePublicShellMinHeight(location.pathname);

  return (
    <TacticoreAuthoringChromeProvider>
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        flex: 1,
        minHeight: 0,
        // Builder owns the column below the site header; prevent document scroll
        // from sliding the edit chrome under the sticky AppBar.
        ...(studioBuilderImmersive
          ? {
              height: STUDIO_BUILDER_LAYOUT_HEIGHT,
              maxHeight: STUDIO_BUILDER_LAYOUT_HEIGHT,
              overflow: 'hidden',
            }
          : {}),
        backgroundColor: 'background.default'
      }}
    >
      <Header />
      {!immersiveChrome ? <ProviderAccessBanner /> : null}
      <Box 
        component="main" 
        role="main"
        aria-label="Main content"
        sx={{ 
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          ...(studioBuilderImmersive ? { overflow: 'hidden' } : {}),
        }}
      >
        <Box
          sx={{
            flex: 1,
            // Homepage + public listings/details: reserve below-fold height so sticky footer never
            // parks in the Lighthouse viewport while React shells resolve (PSI CLS on footer).
            minHeight: studioBuilderImmersive
              ? 0
              : reserveMainMinHeight
                ? PUBLIC_SHELL_MIN_HEIGHT
                : 0,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            px: isHomePage || immersiveChrome ? 0 : AGENCY_SHELL_HORIZONTAL_PX,
            ...(studioBuilderImmersive ? { overflow: 'hidden', minHeight: 0 } : {}),
            '& > *': { width: '100%', minWidth: 0 },
          }}
        >
          {/* Top AdSense Layout reserve removed — empty 120px band sat above
              breadcrumbs without hosting a real unit. In-flow APT slots use
              AdvertisementDisplay; Auto Ads inject their own nodes. */}
          <Suspense fallback={<LayoutRouteFallback />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
      {tacticoreAuthoringImmersive ? (
        <TacticoreAuthoringMiniFooter />
      ) : !studioBuilderImmersive ? (
        <Footer />
      ) : null}
      <ProviderAcquisitionPopupManager />
      <TrainingAlertsNudgeManager />
      
      {/* Bug Report Dialog - Show on all pages */}
      <BugReportDialog 
        open={bugReportHandler.dialogOpen} 
        onClose={bugReportHandler.handleClose} 
        getCapturedLogs={bugReportHandler.getCapturedLogs} 
        initialScreenshot={bugReportHandler.capturedScreenshot}
      />
      
      {/* Unified Help Speed Dial - Show on all pages (guests and authenticated users) */}
      {/* Component handles subdomain exclusion internally (K9 and backoffice) */}
      <SpeedDialErrorBoundary>
        <UnifiedHelpSpeedDial
          onBugReport={bugReportHandler.handleOpen}
          showBugReport={true}
          showChat={true}
          showTours={!isHomePage} // Hide tours on home page
        />
      </SpeedDialErrorBoundary>
    </Box>
    </TacticoreAuthoringChromeProvider>
  );
};

export default Layout;
